import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import Image from "../components/Image";
import { qrCodesApi } from "../utils/api";

type GenerateQRModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (data: any) => void;
  isGenerating: boolean;
};

const GenerateQRModal: React.FC<GenerateQRModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [dotsStyleColor, setDotsStyleColor] = useState("#000000");
  const [dotsStyleType, setDotsStyleType] = useState("square");
  const [cornerSquareColor, setCornerSquareColor] = useState("#000000");
  const [cornerSquareType, setCornerSquareType] = useState("square");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [mode, setMode] = useState<"generate" | "preview">("generate");
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const isPreviewMode = mode === "preview";

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setQuantity(1);
      setDotsStyleColor("#000000");
      setDotsStyleType("square");
      setCornerSquareColor("#000000");
      setCornerSquareType("square");
      setBackgroundColor("#FFFFFF");
      setMode("generate");
      setPreviewDataUrl(null);
      setIsPreviewing(false);
      setPreviewError(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== "preview") {
      setPreviewDataUrl(null);
      setIsPreviewing(false);
      setPreviewError(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      return;
    }

    let isActive = true;
    setIsPreviewing(true);
    setPreviewError(null);

    const timeoutId = window.setTimeout(() => {
      const payload = {
        dotsStyle: { color: dotsStyleColor, type: dotsStyleType },
        cornerSquareStyle: { type: cornerSquareType, color: cornerSquareColor },
        backgroundStyle: { color: backgroundColor },
        imageStyle: { crossOrigin: "anonymous", margin: 10, imageSize: 0.2 },
      };

      qrCodesApi
        .previewQrCode(payload)
        .then((blob) => {
          if (!isActive) return;
          const url = URL.createObjectURL(blob);
          if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
          }
          previewUrlRef.current = url;
          setPreviewDataUrl(url);
        })
        .catch((err) => {
          if (!isActive) return;
          console.error("Failed to render QR preview:", err);
          setPreviewError("Preview could not be generated.");
        })
        .finally(() => {
          if (!isActive) return;
          setIsPreviewing(false);
        });
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    mode,
    dotsStyleColor,
    dotsStyleType,
    cornerSquareColor,
    cornerSquareType,
    backgroundColor,
  ]);

  const handleGenerate = () => {
    onGenerate({
      quantity,
      dotsStyle: { color: dotsStyleColor, type: dotsStyleType },
      cornerSquare: { color: cornerSquareColor, type: cornerSquareType },
      backgroundColor,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`p-0 ${isPreviewMode ? "sm:max-w-[900px] max-h-[90vh] overflow-hidden" : "sm:max-w-[500px]"}`}
      >
        <DialogHeader className={`${isPreviewMode ? "p-4 pb-3" : "p-6 pb-4"}`}>
          <DialogTitle className="text-2xl font-bold">Generate QR Codes</DialogTitle>
        </DialogHeader>
        <div
          className={`${isPreviewMode ? "px-4 py-3" : "px-6 py-4"} border-y ${
            isPreviewMode
              ? "grid gap-4 lg:grid-cols-[1fr_300px] min-h-0"
              : "space-y-6"
          }`}
        >
          <div className={isPreviewMode ? "space-y-4" : "space-y-6"}>
            <div className="flex items-center gap-4">
              <Label className="w-24 text-right text-base">Mode</Label>
              <Select value={mode} onValueChange={(value) => setMode(value as "generate" | "preview")}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generate">Generate</SelectItem>
                  <SelectItem value="preview">Preview</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              <Label htmlFor="quantity" className="w-24 text-right text-base">
                Quantity
              </Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="flex-1"
                min={1}
              />
            </div>

            <Card className="shadow-none border-dashed bg-muted/20">
              <CardHeader>
                <CardTitle className="text-lg">Dots Style</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="w-24 text-right text-base">Color</Label>
                  <Input
                    type="color"
                    value={dotsStyleColor}
                    onChange={(e) => setDotsStyleColor(e.target.value)}
                    className="w-20 h-10 p-1 border-none cursor-pointer"
                    style={{ background: "none" }}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label className="w-24 text-right text-base">Type</Label>
                  <Select
                    value={dotsStyleType}
                    onValueChange={(value) => setDotsStyleType(value)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rounded">Rounded</SelectItem>
                      <SelectItem value="square">Square</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none border-dashed bg-muted/20">
              <CardHeader>
                <CardTitle className="text-lg">Corner Square Style</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="w-24 text-right text-base">Color</Label>
                  <Input
                    type="color"
                    value={cornerSquareColor}
                    onChange={(e) => setCornerSquareColor(e.target.value)}
                    className="w-20 h-10 p-1 border-none cursor-pointer"
                    style={{ background: "none" }}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label className="w-24 text-right text-base">Type</Label>
                  <Select
                    value={cornerSquareType}
                    onValueChange={(value) => setCornerSquareType(value)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="extra-rounded">Extra Rounded</SelectItem>
                      <SelectItem value="square">Square</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center gap-4">
              <Label htmlFor="backgroundColor" className="w-24 text-right text-base">
                Background Color
              </Label>
              <Input
                id="backgroundColor"
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="flex-1 w-20 h-10 p-1 border-none cursor-pointer"
                style={{ background: "none" }}
              />
            </div>
          </div>

          {isPreviewMode && (
            <Card className="shadow-none border-dashed bg-muted/20 h-full">
              <CardHeader>
                <CardTitle className="text-lg">Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-center rounded-lg border bg-background p-4">
                  {isPreviewing ? (
                    <div className="text-sm text-muted-foreground">Rendering preview...</div>
                  ) : previewError ? (
                    <div className="text-sm text-destructive">{previewError}</div>
                  ) : previewDataUrl ? (
                    <Image
                      fileId={previewDataUrl}
                      alt="QR code preview"
                      className="w-40 h-40"
                      fallbackImage=""
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground">Preview unavailable.</div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Preview is generated by the backend for an exact match.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter className={isPreviewMode ? "p-4 pt-3" : "p-6 pt-4"}>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateQRModal;
